import React, { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import api from '../services/api';
import './Enquiries.css';

type EnquiryStatus = 'new' | 'contacted' | 'closed';

interface Enquiry {
  id: number;
  name: string;
  email: string | null;
  phone: string | null;
  service: string | null;
  message: string;
  status: EnquiryStatus;
  createdAt: string;
  updatedAt: string;
}

type Filter = 'all' | 'new' | 'contacted' | 'closed';

const Enquiries: React.FC = () => {
  const { logout } = useAuth();
  const navigate = useNavigate();
  const [enquiries, setEnquiries] = useState<Enquiry[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [filter, setFilter] = useState<Filter>('all');

  useEffect(() => {
    fetchEnquiries();
  }, []);

  const fetchEnquiries = async () => {
    try {
      setLoading(true);
      const res = await api.get('/enquiries');
      setEnquiries(res.data);
    } catch (err: any) {
      setError(err.response?.data?.message || 'Failed to load enquiries');
    } finally {
      setLoading(false);
    }
  };

  const updateStatus = async (id: number, status: EnquiryStatus) => {
    try {
      await api.patch(`/enquiries/${id}/status`, { status });
      setEnquiries(enquiries.map(e => (e.id === id ? { ...e, status } : e)));
    } catch (err: any) {
      alert(err.response?.data?.message || 'Failed to update status');
    }
  };

  const deleteEnquiry = async (id: number, name: string) => {
    if (!window.confirm(`Delete enquiry from "${name}"? This cannot be undone.`)) return;
    try {
      await api.delete(`/enquiries/${id}`);
      setEnquiries(enquiries.filter(e => e.id !== id));
    } catch (err: any) {
      alert(err.response?.data?.message || 'Failed to delete enquiry');
    }
  };

  const filtered = enquiries.filter(e => (filter === 'all' ? true : e.status === filter));

  // Group by date (YYYY-MM-DD of createdAt)
  const groups: { dateKey: string; label: string; items: Enquiry[] }[] = [];
  const groupMap: Record<string, Enquiry[]> = {};
  filtered.forEach(e => {
    const d = new Date(e.createdAt);
    const key = d.toISOString().slice(0, 10);
    if (!groupMap[key]) groupMap[key] = [];
    groupMap[key].push(e);
  });
  Object.keys(groupMap)
    .sort((a, b) => (a < b ? 1 : -1))
    .forEach(key => {
      const d = new Date(key + 'T00:00:00');
      const label = d.toLocaleDateString(undefined, {
        weekday: 'long',
        year: 'numeric',
        month: 'long',
        day: 'numeric',
      });
      groups.push({ dateKey: key, label, items: groupMap[key] });
    });

  const counts = {
    all: enquiries.length,
    new: enquiries.filter(e => e.status === 'new').length,
    contacted: enquiries.filter(e => e.status === 'contacted').length,
    closed: enquiries.filter(e => e.status === 'closed').length,
  };

  const formatTime = (iso: string) =>
    new Date(iso).toLocaleTimeString(undefined, { hour: '2-digit', minute: '2-digit' });

  return (
    <div className="enq-page">
      <header className="enq-header">
        <div className="enq-header-left">
          <h1>arcivi</h1>
          <span className="enq-badge">Enquiries</span>
        </div>
        <div className="enq-header-right">
          <button className="enq-btn-ghost" onClick={() => navigate('/dashboard')}>Dashboard</button>
          <button className="enq-btn-ghost" onClick={logout}>Logout</button>
        </div>
      </header>

      <div className="enq-content">
        <div className="enq-title">
          <h2>Website Enquiries</h2>
          {counts.new > 0 && <span className="enq-new-badge">{counts.new} new</span>}
        </div>

        <div className="enq-tabs">
          <button className={filter === 'all' ? 'enq-tab active' : 'enq-tab'} onClick={() => setFilter('all')}>
            All ({counts.all})
          </button>
          <button className={filter === 'new' ? 'enq-tab active' : 'enq-tab'} onClick={() => setFilter('new')}>
            New ({counts.new})
          </button>
          <button className={filter === 'contacted' ? 'enq-tab active' : 'enq-tab'} onClick={() => setFilter('contacted')}>
            Contacted ({counts.contacted})
          </button>
          <button className={filter === 'closed' ? 'enq-tab active' : 'enq-tab'} onClick={() => setFilter('closed')}>
            Closed ({counts.closed})
          </button>
        </div>

        {loading && <div className="enq-loading">Loading enquiries…</div>}
        {error && <div className="enq-error">{error}</div>}

        {!loading && !error && filtered.length === 0 && (
          <div className="enq-empty">No enquiries to show.</div>
        )}

        {groups.map(group => (
          <div key={group.dateKey} className="enq-group">
            <h3 className="enq-group-label">{group.label}</h3>
            <div className="enq-list">
              {group.items.map(e => (
                <div key={e.id} className={`enq-card enq-card--${e.status}`}>
                  <div className="enq-card-top">
                    <div className="enq-avatar">{e.name.charAt(0).toUpperCase()}</div>
                    <div className="enq-who">
                      <h4>{e.name}</h4>
                      <div className="enq-meta">
                        {e.phone && <span>📞 <a href={`tel:${e.phone}`}>{e.phone}</a></span>}
                        {e.email && <span>✉️ <a href={`mailto:${e.email}`}>{e.email}</a></span>}
                      </div>
                    </div>
                    <div className="enq-status">
                      <span className={`enq-chip enq-chip--${e.status}`}>{e.status}</span>
                      <span className="enq-time">{formatTime(e.createdAt)}</span>
                    </div>
                  </div>

                  {e.service && <div className="enq-service"><strong>Service:</strong> {e.service}</div>}
                  <div className="enq-message">{e.message}</div>

                  <div className="enq-actions">
                    {e.status !== 'contacted' && (
                      <button className="enq-btn enq-btn--contacted" onClick={() => updateStatus(e.id, 'contacted')}>
                        Mark Contacted
                      </button>
                    )}
                    {e.status !== 'closed' && (
                      <button className="enq-btn enq-btn--closed" onClick={() => updateStatus(e.id, 'closed')}>
                        Close
                      </button>
                    )}
                    {e.status !== 'new' && (
                      <button className="enq-btn enq-btn--reopen" onClick={() => updateStatus(e.id, 'new')}>
                        Reopen
                      </button>
                    )}
                    <button className="enq-btn enq-btn--delete" onClick={() => deleteEnquiry(e.id, e.name)}>
                      Delete
                    </button>
                  </div>
                </div>
              ))}
            </div>
          </div>
        ))}
      </div>
    </div>
  );
};

export default Enquiries;
